"use client";

/**
 * Last-resort error screen for a crash in the ROOT LAYOUT.
 *
 * WHY THIS FILE EXISTS. Without it, a root-layout crash renders Next's built-in
 * "Application error: a client-side exception has occurred (see the browser
 * console for more information)". On a phone — and especially inside the native
 * WebView — there IS no browser console to see. The app is dead, the screen
 * says so, and it offers the user nothing: no message, no reload, no way back.
 * That happened for real, and it cost hours of guessing at a bug the app
 * already knew the answer to.
 *
 * So: say what broke, and give a way out. `digest` is the only identifier that
 * survives to the server logs, so it is shown even when the message is minified
 * away in production.
 *
 * NOTE this replaces the root layout, so it must render its own <html>/<body>
 * and cannot rely on globals.css having loaded — hence the inline styles.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // A crash in the layout usually means the loaded build is unusable, so give
  // the user the recovery that actually works — drop the service worker and
  // caches, then reload — rather than a reset() that re-renders the same
  // broken tree.
  const hardReset = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      const regs = await navigator.serviceWorker?.getRegistrations?.();
      await Promise.all((regs ?? []).map((r) => r.unregister()));
    } catch {
      /* best-effort — reload regardless */
    }
    window.location.reload();
  };

  const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } as const;

  return (
    <html lang="en">
      <body style={{ margin: 0, background: "#0b0f14", color: "#e6edf3", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px" }}>
          <h1 style={{ fontSize: 20, margin: "0 0 8px" }}>HarryClaw hit an error</h1>
          <p style={{ color: "#9fb0c0", fontSize: 14, margin: "0 0 20px" }}>
            Something in the app shell failed to start. The details below are what to send me.
          </p>

          <div
            style={{
              ...mono,
              background: "#11161d",
              border: "1px solid #263140",
              borderRadius: 8,
              padding: 14,
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowX: "auto",
            }}
          >
            <div style={{ color: "#ff8a8a" }}>{error?.name ?? "Error"}: {error?.message ?? "(no message)"}</div>
            {error?.digest ? <div style={{ color: "#9fb0c0", marginTop: 8 }}>digest: {error.digest}</div> : null}
            {error?.stack ? (
              <div style={{ color: "#7f92a5", marginTop: 8 }}>{error.stack.split("\n").slice(0, 12).join("\n")}</div>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
            <button
              onClick={() => void hardReset()}
              style={{ background: "#2f81f7", color: "#fff", border: 0, borderRadius: 6, padding: "10px 14px", fontSize: 14, fontWeight: 600 }}
            >
              Clear cache &amp; reload
            </button>
            <button
              onClick={() => reset()}
              style={{ background: "transparent", color: "#9fb0c0", border: "1px solid #263140", borderRadius: 6, padding: "10px 14px", fontSize: 14 }}
            >
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
